import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';

const privacyContent = {
  en: {
    title: 'Privacy Policy',
    lastUpdated: 'Last updated: January 2026',
    backToSite: 'Back to Site',
    sections: [
      {
        title: '1. Introduction',
        content: 'This Privacy Policy explains how we collect, use, and protect your personal data when you use our website skopelos-cottages.gr. We are committed to protecting your privacy and complying with the General Data Protection Regulation (GDPR).'
      },
      {
        title: '2. Data Controller',
        content: 'The data controller responsible for your personal data is the property owner. For any privacy-related inquiries, please contact us at petrosperimenis [at] yahoo [dot] gr.'
      },
      {
        title: '3. Data We Collect',
        content: 'When you use our contact form, we collect:\n• Your name (from Google account)\n• Your email address (from Google account)\n• Your phone number (optional, if provided)\n• Your message\n• Your IP address (for security purposes)\n• Timestamp of submission\n• Your privacy consent and its timestamp\n\nWhen you use our AI Concierge chatbot, we collect:\n• The text of the messages you send\n• The chatbot\'s replies\n• A hashed (irreversible) version of your IP address for abuse prevention\n• Your selected language\n• Your explicit chat consent and timestamp\n• If you choose to leave your details inside the chat: your name, email, and optional note\n\nWe also collect anonymous analytics data through Google Analytics (only if you accept Statistics cookies), including pages visited, time on site, and general location (country/city level).'
      },
      {
        title: '4. Purpose of Data Collection',
        content: 'We use your personal data to:\n• Respond to your property inquiries\n• Communicate with you about the property\n• Provide AI-assisted answers via the chatbot\n• Protect against spam and fraudulent submissions\n• Improve our website through analytics (only with your consent)'
      },
      {
        title: '5. Legal Basis',
        content: 'We process your data based on:\n• Your explicit consent (when submitting the contact form and when starting a chat with the AI Concierge)\n• Our legitimate interest in responding to inquiries and protecting our website from abuse'
      },
      {
        title: '6. Data Retention',
        content: 'We apply the following specific retention periods, after which data is automatically and permanently deleted by a daily cleanup job:\n\n• Chat messages and chat sessions: 30 days\n• Chat leads (name + email left through the chatbot): 18 months\n• Contact form submissions: 18 months or until the property is sold, whichever comes first\n• Admin sessions: deleted as soon as they expire (24 hours)\n• Analytics data: retained by Google Analytics for 14 months (default setting)\n\nYou may request earlier deletion at any time by emailing us — see "Your Rights" below.'
      },
      {
        title: '7. Data Sharing',
        content: 'We do not sell your personal data. We may share your data with:\n• Google (for authentication and analytics, only with your consent)\n• Our email service provider (Resend) for sending notifications to the property owner\n• Our AI service provider (Anthropic, via the Emergent platform proxy) — solely to generate chatbot responses in real time. Your messages are not used to train any AI model\n• Real estate agencies and licensed real estate agents we collaborate with, for the purpose of contacting you regarding your inquiry (e.g., to arrange property viewings, provide additional information, or assist with the purchase process)\n\nAll third parties are bound by confidentiality obligations and may only use your data for the specific purposes outlined above.'
      },
      {
        title: '8. Your Rights',
        content: 'Under GDPR, you have the right to:\n• Access your personal data\n• Correct inaccurate data\n• Request deletion of your data\n• Object to processing\n• Data portability\n• Withdraw consent at any time\n\nTo exercise these rights, contact us at petrosperimenis [at] yahoo [dot] gr.'
      },
      {
        title: '9. Cookies',
        content: 'We use cookies for:\n• Google Analytics (anonymous usage statistics)\n• Session management (authentication)\n\nYou can control cookies through your browser settings or our cookie consent banner.'
      },
      {
        title: '10. Security',
        content: 'We implement appropriate security measures including:\n• SSL/TLS encryption\n• Secure authentication via Google OAuth\n• Password-protected admin access\n• reCAPTCHA protection against bots'
      },
      {
        title: '11. Changes to This Policy',
        content: 'We may update this policy from time to time. Any changes will be posted on this page with an updated revision date.'
      },
      {
        title: '12. Contact',
        content: 'For any questions about this Privacy Policy or your personal data, please contact:\n\nEmail: petrosperimenis [at] yahoo [dot] gr\nLocation: Skopelos, Greece'
      }
    ]
  },
  gr: {
    title: 'Πολιτική Απορρήτου',
    lastUpdated: 'Τελευταία ενημέρωση: Ιανουάριος 2026',
    backToSite: 'Επιστροφή στον Ιστότοπο',
    sections: [
      {
        title: '1. Εισαγωγή',
        content: 'Αυτή η Πολιτική Απορρήτου εξηγεί πώς συλλέγουμε, χρησιμοποιούμε και προστατεύουμε τα προσωπικά σας δεδομένα όταν χρησιμοποιείτε τον ιστότοπό μας skopelos-cottages.gr. Δεσμευόμαστε να προστατεύουμε το απόρρητό σας και να συμμορφωνόμαστε με τον Γενικό Κανονισμό Προστασίας Δεδομένων (GDPR).'
      },
      {
        title: '2. Υπεύθυνος Επεξεργασίας',
        content: 'Ο υπεύθυνος επεξεργασίας των προσωπικών σας δεδομένων είναι ο ιδιοκτήτης του ακινήτου. Για ερωτήσεις σχετικά με το απόρρητο, επικοινωνήστε μαζί μας στο petrosperimenis [at] yahoo [dot] gr.'
      },
      {
        title: '3. Δεδομένα που Συλλέγουμε',
        content: 'Όταν χρησιμοποιείτε τη φόρμα επικοινωνίας, συλλέγουμε:\n• Το όνομά σας (από τον λογαριασμό Google)\n• Τη διεύθυνση email σας (από τον λογαριασμό Google)\n• Τον αριθμό τηλεφώνου σας (προαιρετικά)\n• Το μήνυμά σας\n• Τη διεύθυνση IP σας (για λόγους ασφαλείας)\n• Χρονοσήμανση υποβολής\n• Τη συγκατάθεση απορρήτου σας και τη χρονοσήμανσή της\n\nΌταν χρησιμοποιείτε τον AI Σύμβουλο (chatbot), συλλέγουμε:\n• Το κείμενο των μηνυμάτων σας\n• Τις απαντήσεις του chatbot\n• Μια κρυπτογραφημένη (μη αναστρέψιμη) εκδοχή της IP σας για αποτροπή κατάχρησης\n• Την επιλεγμένη γλώσσα σας\n• Την ρητή σας συγκατάθεση για τη συνομιλία και τη χρονοσήμανση\n• Εάν επιλέξετε να αφήσετε τα στοιχεία σας μέσα στο chatbot: όνομα, email και προαιρετική σημείωση\n\nΣυλλέγουμε επίσης ανώνυμα δεδομένα αναλυτικών μέσω του Google Analytics (μόνο εάν αποδεχθείτε τα cookies στατιστικών).'
      },
      {
        title: '4. Σκοπός Συλλογής Δεδομένων',
        content: 'Χρησιμοποιούμε τα προσωπικά σας δεδομένα για:\n• Να απαντήσουμε στα ερωτήματά σας για το ακίνητο\n• Να επικοινωνήσουμε μαζί σας σχετικά με το ακίνητο\n• Να παρέχουμε απαντήσεις μέσω AI chatbot\n• Να προστατευτούμε από spam και καταχρήσεις\n• Να βελτιώσουμε τον ιστότοπό μας (μόνο με τη συγκατάθεσή σας)'
      },
      {
        title: '5. Νομική Βάση',
        content: 'Επεξεργαζόμαστε τα δεδομένα σας βάσει:\n• Της ρητής συγκατάθεσής σας (κατά την υποβολή της φόρμας επικοινωνίας και κατά την έναρξη συνομιλίας με τον AI Σύμβουλο)\n• Του έννομου συμφέροντός μας να απαντάμε σε ερωτήματα και να προστατεύουμε τον ιστότοπό μας από κατάχρηση'
      },
      {
        title: '6. Διατήρηση Δεδομένων',
        content: 'Εφαρμόζουμε τις ακόλουθες συγκεκριμένες περιόδους διατήρησης. Μετά την παρέλευσή τους, τα δεδομένα διαγράφονται αυτόματα και οριστικά από καθημερινή εργασία καθαρισμού:\n\n• Μηνύματα chatbot και συνεδρίες: 30 ημέρες\n• Στοιχεία επικοινωνίας από το chatbot (όνομα + email): 18 μήνες\n• Υποβολές φόρμας επικοινωνίας: 18 μήνες ή μέχρι την πώληση του ακινήτου\n• Συνεδρίες διαχειριστή: διαγράφονται μόλις λήξουν (24 ώρες)\n• Δεδομένα analytics: διατηρούνται από Google Analytics για 14 μήνες\n\nΜπορείτε να ζητήσετε νωρίτερη διαγραφή ανά πάσα στιγμή — βλ. "Τα Δικαιώματά σας" παρακάτω.'
      },
      {
        title: '7. Κοινοποίηση Δεδομένων',
        content: 'Δεν πωλούμε τα προσωπικά σας δεδομένα. Μπορεί να τα μοιραστούμε με:\n• Google (για ταυτοποίηση και analytics, μόνο με τη συγκατάθεσή σας)\n• Τον πάροχο email μας (Resend) για αποστολή ειδοποιήσεων\n• Τον πάροχο AI (Anthropic, μέσω της πλατφόρμας Emergent) — αποκλειστικά για την παραγωγή απαντήσεων του chatbot σε πραγματικό χρόνο. Τα μηνύματά σας δεν χρησιμοποιούνται για εκπαίδευση μοντέλων AI\n• Μεσιτικά γραφεία και αδειοδοτημένους μεσίτες ακινήτων με τους οποίους συνεργαζόμαστε, με σκοπό να επικοινωνήσουν μαζί σας σχετικά με το ερώτημά σας (π.χ., για να κανονίσουν επίσκεψη στο ακίνητο, να παρέχουν επιπλέον πληροφορίες ή να βοηθήσουν με τη διαδικασία αγοράς)\n\nΌλοι οι τρίτοι δεσμεύονται από υποχρεώσεις εμπιστευτικότητας και μπορούν να χρησιμοποιήσουν τα δεδομένα σας μόνο για τους συγκεκριμένους σκοπούς που αναφέρονται παραπάνω.'
      },
      {
        title: '8. Τα Δικαιώματά σας',
        content: 'Σύμφωνα με τον GDPR, έχετε δικαίωμα:\n• Πρόσβασης στα δεδομένα σας\n• Διόρθωσης ανακριβών δεδομένων\n• Διαγραφής των δεδομένων σας\n• Εναντίωσης στην επεξεργασία\n• Φορητότητας δεδομένων\n• Ανάκλησης συγκατάθεσης\n\nΓια να ασκήσετε αυτά τα δικαιώματα, επικοινωνήστε στο petrosperimenis [at] yahoo [dot] gr.'
      },
      {
        title: '9. Cookies',
        content: 'Χρησιμοποιούμε cookies για:\n• Google Analytics (ανώνυμα στατιστικά)\n• Διαχείριση συνεδρίας (ταυτοποίηση)\n\nΜπορείτε να ελέγξετε τα cookies μέσω του προγράμματος περιήγησής σας.'
      },
      {
        title: '10. Ασφάλεια',
        content: 'Εφαρμόζουμε κατάλληλα μέτρα ασφαλείας:\n• Κρυπτογράφηση SSL/TLS\n• Ασφαλής ταυτοποίηση μέσω Google OAuth\n• Προστασία admin με κωδικό\n• Προστασία reCAPTCHA'
      },
      {
        title: '11. Αλλαγές σε Αυτήν την Πολιτική',
        content: 'Ενδέχεται να ενημερώσουμε αυτήν την πολιτική. Οποιεσδήποτε αλλαγές θα δημοσιεύονται σε αυτήν τη σελίδα.'
      },
      {
        title: '12. Επικοινωνία',
        content: 'Για ερωτήσεις σχετικά με αυτήν την Πολιτική Απορρήτου, επικοινωνήστε:\n\nEmail: petrosperimenis [at] yahoo [dot] gr\nΤοποθεσία: Σκόπελος, Ελλάδα'
      }
    ]
  },
  de: {
    title: 'Datenschutzerklärung',
    lastUpdated: 'Zuletzt aktualisiert: Januar 2026',
    backToSite: 'Zurück zur Website',
    sections: [
      {
        title: '1. Einleitung',
        content: 'Diese Datenschutzerklärung erläutert, wie wir Ihre personenbezogenen Daten erheben, verwenden und schützen, wenn Sie unsere Website skopelos-cottages.gr nutzen. Wir verpflichten uns zum Schutz Ihrer Privatsphäre und zur Einhaltung der Datenschutz-Grundverordnung (DSGVO).'
      },
      {
        title: '2. Verantwortlicher',
        content: 'Der für Ihre personenbezogenen Daten Verantwortliche ist der Immobilieneigentümer. Bei datenschutzbezogenen Anfragen kontaktieren Sie uns bitte unter petrosperimenis [at] yahoo [dot] gr.'
      },
      {
        title: '3. Daten, die wir erheben',
        content: 'Wenn Sie unser Kontaktformular nutzen, erheben wir:\n• Ihren Namen (von Google-Konto)\n• Ihre E-Mail-Adresse (von Google-Konto)\n• Ihre Telefonnummer (optional)\n• Ihre Nachricht\n• Ihre IP-Adresse (zu Sicherheitszwecken)\n• Zeitstempel der Übermittlung\n• Ihre Datenschutzeinwilligung und deren Zeitstempel\n\nWenn Sie den KI-Concierge (Chatbot) nutzen, erheben wir:\n• Den Text Ihrer Nachrichten\n• Die Antworten des Chatbots\n• Eine gehashte (nicht umkehrbare) Version Ihrer IP-Adresse zur Missbrauchsprävention\n• Ihre gewählte Sprache\n• Ihre ausdrückliche Chat-Einwilligung und Zeitstempel\n• Falls Sie im Chat Ihre Daten hinterlassen: Name, E-Mail und optionale Notiz'
      },
      {
        title: '4. Zweck der Datenerhebung',
        content: 'Wir verwenden Ihre personenbezogenen Daten, um:\n• Auf Ihre Immobilienanfragen zu antworten\n• Mit Ihnen über die Immobilie zu kommunizieren\n• KI-gestützte Antworten über den Chatbot zu liefern\n• Uns vor Spam und Missbrauch zu schützen\n• Unsere Website zu verbessern (nur mit Ihrer Einwilligung)'
      },
      {
        title: '6. Datenspeicherung',
        content: 'Wir wenden folgende konkrete Aufbewahrungsfristen an. Danach werden die Daten täglich durch einen automatischen Bereinigungsjob endgültig gelöscht:\n\n• Chat-Nachrichten und Chat-Sitzungen: 30 Tage\n• Chat-Leads (Name + E-Mail aus dem Chatbot): 18 Monate\n• Kontaktformular-Übermittlungen: 18 Monate oder bis zum Verkauf der Immobilie\n• Admin-Sitzungen: gelöscht, sobald sie ablaufen (24 Stunden)\n• Analytics-Daten: von Google Analytics 14 Monate aufbewahrt\n\nSie können jederzeit eine frühere Löschung anfordern.'
      },
      {
        title: '8. Ihre Rechte',
        content: 'Nach der DSGVO haben Sie das Recht auf:\n• Auskunft über Ihre Daten\n• Berichtigung unrichtiger Daten\n• Löschung Ihrer Daten\n• Widerspruch gegen die Verarbeitung\n• Datenübertragbarkeit\n• Widerruf der Einwilligung\n\nKontaktieren Sie uns unter petrosperimenis [at] yahoo [dot] gr.'
      },
      {
        title: '12. Kontakt',
        content: 'Bei Fragen zu dieser Datenschutzerklärung kontaktieren Sie:\n\nE-Mail: petrosperimenis [at] yahoo [dot] gr\nStandort: Skopelos, Griechenland'
      }
    ]
  }
};

// Fallback to English for other languages
const getContent = (language) => {
  return privacyContent[language] || privacyContent.en;
};

const PrivacyPolicy = () => {
  const { language } = useLanguage();
  const content = getContent(language);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-800"
            data-testid="privacy-back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            {content.backToSite}
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-slate-800 mb-2">{content.title}</h1>
        <p className="text-slate-500 mb-8">{content.lastUpdated}</p>

        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">
          {content.sections.map((section, index) => (
            <section key={index}>
              <h2 className="text-xl font-semibold text-slate-800 mb-3">{section.title}</h2>
              <p className="text-slate-600 whitespace-pre-line leading-relaxed">{section.content}</p>
            </section>
          ))}

          {/* GDPR self-serve deletion CTA */}
          <section className="bg-gradient-to-br from-[#fff7f1] to-[#fceee2] border border-[#d4845f]/30 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-slate-800 mb-2">
              Self-Serve Data Deletion
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              You can request immediate, automated deletion of all personal data we hold about you
              using our dedicated form — no email back-and-forth required.
            </p>
            <Button
              onClick={() => navigate('/data-deletion')}
              className="bg-[#d4845f] hover:bg-[#c17552] text-white"
              data-testid="privacy-deletion-cta"
            >
              Go to Data Deletion →
            </Button>
          </section>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
